import styles from '@ui/components/InputField/InputField.module.scss';
import Content from '@ui/components/Content/Content';
import PropTypes from 'prop-types';
import React, { forwardRef } from 'react';
import {
  FormDescription,
  FormInput,
  FormError,
  FormLabel,
} from '@ariakit/react';
import classNames from 'classnames';

const InputMessage = ({ name, store }) => (
  <FormError
    aria-atomic="true"
    className={styles.message}
    name={store.names[name]}
    store={store}
  />
);

const AffixInputWrapper = ({
  children,
  prefix,
  suffix,
  disabled,
  name,
  store,
}) => {
  const hasPrefix = !!prefix;
  const hasSuffix = !!suffix;

  if (!hasPrefix && !hasSuffix) return children;

  return (
    <span
      className={classNames(styles.inputWrapper, {
        [styles.hasSuffix]: hasSuffix,
        [styles.hasPrefix]: hasPrefix,
        [styles.isDisabled]: disabled,
      })}
    >
      {hasPrefix && (
        <FormDescription className={styles.prefix} name={name} store={store}>
          {prefix}
        </FormDescription>
      )}
      {children}
      {hasSuffix && (
        <FormDescription className={styles.suffix} name={name} store={store}>
          {suffix}
        </FormDescription>
      )}
    </span>
  );
};

const InputField = forwardRef(
  (
    {
      className,
      description,
      disabled = false,
      inputMode = 'text',
      label,
      labelHidden = false,
      name,
      noMessage = false,
      onKeyPress = () => {},
      placeholder,
      prefix,
      required = false,
      store,
      suffix,
      type = 'text',
      variant = 'default',
      ...props
    },
    ref,
  ) => (
    <div className={classNames(styles.container, className)}>
      <FormLabel
        className={classNames(styles.label, styles[variant], {
          [styles.hidden]: labelHidden,
        })}
        name={store.names[name]}
        store={store}
      >
        {label}
      </FormLabel>
      <AffixInputWrapper
        disabled={disabled}
        name={store.names[name]}
        prefix={prefix}
        store={store}
        suffix={suffix}
      >
        <FormInput
          aria-required={required}
          className={classNames(styles.input, styles[variant], {
            [styles.hasSuffix]: suffix,
            [styles.hasPrefix]: prefix,
          })}
          disabled={disabled}
          inputMode={inputMode}
          name={store.names[name]}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          ref={ref}
          required={required}
          store={store}
          type={type}
          {...props}
        />
      </AffixInputWrapper>
      {description && (
        <FormDescription
          name={store.names[name]}
          render={
            <Content className={styles.description} noSpacing withParser>
              {description}
            </Content>
          }
          store={store}
        />
      )}
      {!noMessage && <InputMessage name={name} store={store} />}
    </div>
  ),
);

InputField.propTypes = {
  className: PropTypes.string,
  description: PropTypes.string,
  disabled: PropTypes.bool,
  inputMode: PropTypes.oneOf([
    'none',
    'text',
    'decimal',
    'numeric',
    'tel',
    'search',
    'email',
    'url',
  ]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  labelHidden: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noMessage: PropTypes.bool,
  onKeyPress: PropTypes.func,
  placeholder: PropTypes.string,
  prefix: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element, // Also accepts an SVG component
  ]),
  required: PropTypes.bool,
  /* eslint-disable react/forbid-prop-types */
  /** `useFormStore()` return value from `@ariakit/react` */
  store: PropTypes.object.isRequired,
  suffix: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element, // Also accepts an SVG component
  ]),
  /* eslint-enable react/forbid-prop-types */
  type: PropTypes.oneOf([
    'button',
    'checkbox',
    'color',
    'date',
    'datetime-local',
    'email',
    'file',
    'hidden',
    'image',
    'month',
    'number',
    'password',
    'radio',
    'range',
    'reset',
    'search',
    'submit',
    'tel',
    'text',
    'time',
    'url',
    'week',
  ]),
  variant: PropTypes.oneOf(['default', 'closed']),
};

InputField.displayName = 'InputField';

export default InputField;
export { InputMessage };
